<div mat-dialog-title>{{ 'Credentials.ChangePassword.Title' | translate:locale.language }}</div>

<div mat-dialog-content>
  <app-loading [hasLoaded]="!loading()" [isCircle]="false"></app-loading>

  <form [formGroup]="passwordForm" style="margin: 0.5rem 0 1rem 0;">
    <!-- Current Password -->
    <mat-form-field class="field">
      <mat-label>{{ 'Credentials.ChangePassword.CurrentPassword' | translate:locale.language }}</mat-label>
      <input
        matInput
        formControlName="currentPassword"
        [type]="hideCurrentPassword() ? 'password' : 'text'"
        required
      >
      <button
        mat-icon-button
        matSuffix
        (click)="togglePasswordVisibility(hideCurrentPassword)"
        [attr.aria-label]="(hideCurrentPassword() ? 'Credentials.ChangePassword.Show': 'Credentials.ChangePassword.Hide') | translate:locale.language"
      >
        <mat-icon>{{ hideCurrentPassword() ? 'visibility' : 'visibility_off' }}</mat-icon>
      </button>
    </mat-form-field>

    <!-- New Password -->
    <mat-form-field class="field" subscriptSizing="dynamic" style="margin-bottom: 22px;">
      <mat-label>{{ 'Credentials.ChangePassword.NewPassword' | translate:locale.language }}</mat-label>
      <input
        matInput
        formControlName="newPassword"
        [type]="hideNewPassword() ? 'password' : 'text'"
        required
        [errorStateMatcher]="unchangedMatcher"
      >
      <button
        mat-icon-button
        matSuffix
        (click)="togglePasswordVisibility(hideNewPassword)"
        [attr.aria-label]="(hideNewPassword() ? 'Credentials.ChangePassword.Show': 'Credentials.ChangePassword.Hide') | translate:locale.language"
      >
        <mat-icon>{{ hideNewPassword() ? 'visibility' : 'visibility_off' }}</mat-icon>
      </button>

      @if (passwordForm.hasError('passwordUnchanged')) {
        <mat-error>- {{ 'Credentials.ChangePassword.Validation.PasswordUnchanged' | translate:locale.language }}</mat-error>
      }
      @for (error of getPasswordPolicyErrors(); track $index) {
        <mat-error>- {{ error | translate:locale.language }}</mat-error>
      }
    </mat-form-field>

    <!-- Confirm Password -->
    <mat-form-field class="field">
      <mat-label>{{ 'Credentials.ChangePassword.ConfirmPassword' | translate:locale.language }}</mat-label>
      <input
        matInput
        formControlName="confirmPassword"
        [type]="hideConfirmPassword() ? 'password' : 'text'"
        required
        [errorStateMatcher]="mismatchMatcher"
      >
      <button
        mat-icon-button
        matSuffix
        (click)="togglePasswordVisibility(hideConfirmPassword)"
        [attr.aria-label]="(hideConfirmPassword() ? 'Credentials.ChangePassword.Show': 'Credentials.ChangePassword.Hide') | translate:locale.language"
      >
        <mat-icon>{{ hideConfirmPassword() ? 'visibility' : 'visibility_off' }}</mat-icon>
      </button>

      @if (passwordForm.hasError('passwordMismatch')) {
        <mat-error>{{ 'Credentials.ChangePassword.Validation.PasswordMismatch' | translate:locale.language }}</mat-error>
      }
    </mat-form-field>
  </form>

  <!-- Api Errors -->
  @if (apiErrors().length) {
    <div>
      <ul class="browser-default">
        @for (error of apiErrors(); track $index) {
          <li class="as-error-text">{{ error }}</li>
        }
      </ul>
    </div>
  }

  <div>
    <mat-accordion multi>
      <!-- Password Requirements -->
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title>{{ 'Credentials.ChangePassword.Requirements' | translate:locale.language }}</mat-panel-title>
        </mat-expansion-panel-header>

        <ul class="browser-default">
          <li>{{ 'Credentials.ChangePassword.Validation.PasswordUnchanged' | translate:locale.language }}</li>
          <li>{{ 'Credentials.ChangePassword.Validation.MinLength' | translate:locale.language }}</li>
          <li>{{ 'Credentials.ChangePassword.Validation.Lowercase' | translate:locale.language }}</li>
          <li>{{ 'Credentials.ChangePassword.Validation.Uppercase' | translate:locale.language }}</li>
          <li>{{ 'Credentials.ChangePassword.Validation.Numbers' | translate:locale.language }}</li>
          <li>{{ 'Credentials.ChangePassword.Validation.Special' | translate:locale.language }}</li>
        </ul>
      </mat-expansion-panel>

      <!-- Password Guidelines -->
      <mat-expansion-panel class="guidelines-panel">
        <mat-expansion-panel-header>
          <mat-panel-title>{{ 'Credentials.ChangePassword.Guidelines.Title' | translate:locale.language }}</mat-panel-title>
        </mat-expansion-panel-header>

        <ul class="browser-default">
          <li>{{ 'Credentials.ChangePassword.Guidelines.SingleWord' | translate:locale.language }}</li>
          <li>{{ 'Credentials.ChangePassword.Guidelines.Identifiable' | translate:locale.language }}</li>
          <li>{{ 'Credentials.ChangePassword.Guidelines.Email' | translate:locale.language }}</li>
          <li>{{ 'Credentials.ChangePassword.Guidelines.Sequence' | translate:locale.language }}</li>
          <li>{{ 'Credentials.ChangePassword.Guidelines.Duplicate' | translate:locale.language }}</li>
        </ul>

        <div mat-subheader>{{ 'Credentials.ChangePassword.Guidelines.Suggestions' | translate:locale.language }}</div>
        <ul class="browser-default">
          <li>{{ 'Credentials.ChangePassword.Guidelines.Phrase' | translate:locale.language }}</li>
          <li>{{ 'Credentials.ChangePassword.Guidelines.Digits' | translate:locale.language }}</li>
          <li>{{ 'Credentials.ChangePassword.Guidelines.Replace' | translate:locale.language }}</li>
          <li>{{ 'Credentials.ChangePassword.Guidelines.Misspell' | translate:locale.language }}</li>
          <li>{{ 'Credentials.ChangePassword.Guidelines.Vowels' | translate:locale.language }}</li>
          <li>{{ 'Credentials.ChangePassword.Guidelines.CaseSensitive' | translate:locale.language }}</li>
        </ul>
      </mat-expansion-panel>
    </mat-accordion>
  </div>
</div>

<!-- Actions -->
<div mat-dialog-actions style="justify-content: end;">
  <button
    mat-button
    color="primary"
    (click)="cancel()"
  >
    {{ 'Cancel' | translate:locale.language }}
  </button>

  <button
    mat-raised-button
    color="primary"
    (click)="submit()"
    [disabled]="passwordForm.invalid || loading()"
  >
    {{ 'Submit' | translate:locale.language }}
  </button>
</div>